import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import supplierPaymentService from '../../../services/supplierPaymentService';
import { FiArrowLeft, FiDollarSign, FiFileText, FiCalendar, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';

const SupplierPendingDetails = () => {
  const { supplierId } = useParams();
  const [supplierData, setSupplierData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedOrder, setExpandedOrder] = useState(null);

  useEffect(() => {
    fetchSupplierData();
  }, [supplierId]);

  const fetchSupplierData = async () => {
    try {
      setLoading(true);
      const data = await supplierPaymentService.getSupplierPendingPayments(supplierId);
      setSupplierData(data);
    } catch (err) {
      setError('Failed to fetch supplier details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  const getPaymentStatusBadge = (balance, totalPaid) => {
    if (balance === 0) {
      return <span className="sp-status-badge sp-status-paid">Paid</span>;
    } else if (totalPaid > 0) {
      return <span className="sp-status-badge sp-status-partial">Partial</span>;
    } else {
      return <span className="sp-status-badge sp-status-pending">Pending</span>;
    }
  };

  if (loading) {
    return (
      <div className="sp-detail-container">
        <div className="sp-detail-skeleton">
          <div className="sp-skeleton-header"></div>
          <div className="sp-skeleton-summary"></div>
          <div className="sp-skeleton-orders"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sp-detail-container">
        <div className="sp-detail-error">
          <h3>{error}</h3>
          <button onClick={fetchSupplierData} className="sp-retry-button">
            Retry
          </button>
          <Link to="/reports/pending-payments" className="sp-back-link">
            <FiArrowLeft /> Back to Report
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="sp-detail-container">
      {/* Header */}
      <div className="sp-detail-header">
        <Link to="/reports/pending-payments" className="sp-back-button">
          <FiArrowLeft /> Back to Report
        </Link>
        <h1 className="sp-detail-title">
          <FiDollarSign /> Pending Payments Details
        </h1>
      </div>

      {/* Supplier Info */}
      <div className="sp-supplier-info-card">
        <div className="sp-supplier-header">
          <h2 className="sp-supplier-name">{supplierData?.supplier?.name}</h2>
          <span className="sp-supplier-code">{supplierData?.supplier?.code}</span>
        </div>
        
        <div className="sp-supplier-contact">
          <div className="sp-contact-item">
            <strong>Contact Person:</strong> {supplierData?.supplier?.contact_person}
          </div>
          <div className="sp-contact-item">
            <strong>Email:</strong> {supplierData?.supplier?.email}
          </div>
          <div className="sp-contact-item">
            <strong>Phone:</strong> {supplierData?.supplier?.phone}
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="sp-detail-summary">
        <div className="sp-summary-highlight">
          <div className="sp-highlight-icon">
            <FiDollarSign />
          </div>
          <div className="sp-highlight-content">
            <div className="sp-highlight-label">Total Pending Amount</div>
            <div className="sp-highlight-value">
              {formatCurrency(supplierData?.summary?.total_pending_amount || 0)}
            </div>
          </div>
        </div>
        
        <div className="sp-summary-stats">
          <div className="sp-stat-item">
            <FiFileText className="sp-stat-icon" />
            <div className="sp-stat-content">
              <div className="sp-stat-label">Pending Orders</div>
              <div className="sp-stat-value">{supplierData?.summary?.pending_orders_count || 0}</div>
            </div>
          </div>
          
          <div className="sp-stat-item">
            <FiCheckCircle className="sp-stat-icon" />
            <div className="sp-stat-content">
              <div className="sp-stat-label">Currency</div>
              <div className="sp-stat-value">{supplierData?.summary?.currency || 'INR'}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Pending Orders */}
      <div className="sp-pending-orders-section">
        <h3 className="sp-section-title">
          <FiFileText /> Pending Purchase Orders
        </h3>
        
        {supplierData?.pending_purchase_orders?.length > 0 ? (
          <div className="sp-orders-list">
            {supplierData.pending_purchase_orders.map((order) => (
              <div key={order.purchase_order_id} className="sp-order-card">
                <div className="sp-order-header">
                  <div className="sp-order-main-info">
                    <h4 className="sp-order-number">{order.order_number}</h4>
                    <span className="sp-order-date">
                      <FiCalendar /> {formatDate(order.order_date)}
                    </span>
                    {order.due_date && (
                      <span className="sp-due-date">
                        Due: {formatDate(order.due_date)}
                      </span>
                    )}
                  </div>
                  {getPaymentStatusBadge(order.balance, order.total_paid)}
                </div>
                
                <div className="sp-order-summary">
                  <div className="sp-summary-item">
                    <span className="sp-summary-label">Total Amount:</span>
                    <span className="sp-summary-value">
                      {formatCurrency(order.total_amount)}
                    </span>
                  </div>
                  <div className="sp-summary-item">
                    <span className="sp-summary-label">Total Paid:</span>
                    <span className="sp-summary-value sp-paid-amount">
                      {formatCurrency(order.total_paid)}
                    </span>
                  </div>
                  <div className="sp-summary-item">
                    <span className="sp-summary-label">Balance Due:</span>
                    <span className="sp-summary-value sp-balance-amount">
                      {formatCurrency(order.balance)}
                    </span>
                  </div>
                </div>
                
                <div className="sp-order-status">
                  <span className={`sp-order-status-badge sp-status-${order.status}`}>
                    {order.status}
                  </span>
                </div>
                
                {/* Expandable Items Section */}
                <button
                  onClick={() => setExpandedOrder(
                    expandedOrder === order.purchase_order_id ? null : order.purchase_order_id
                  )}
                  className="sp-toggle-items"
                >
                  {expandedOrder === order.purchase_order_id ? 'Hide Items' : 'Show Items'} →
                </button>
                
                {expandedOrder === order.purchase_order_id && (
                  <div className="sp-order-items">
                    <h5>Order Items:</h5>
                    <table className="sp-items-table">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Quantity</th>
                          <th>Unit Price</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.items?.map((item, index) => (
                          <tr key={index}>
                            <td>{item.product_name}</td>
                            <td>{item.quantity}</td>
                            <td>{formatCurrency(item.unit_price)}</td>
                            <td>{formatCurrency(item.total_price)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                
                <div className="sp-order-actions">
                  <Link
                    to={`/supplier-payments/new?order=${order.purchase_order_id}`}
                    className="sp-make-payment-button"
                  >
                    Make Payment
                  </Link>
                  <Link
                    to={`/purchase-orders/${order.purchase_order_id}`}
                    className="sp-view-order-button"
                  >
                    View Order
                  </Link>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="sp-no-pending">
            <FiCheckCircle className="sp-no-data-icon" />
            <p>No pending payments for this supplier</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierPendingDetails;